import { createApp } from 'vue'

import plugins, { setupPlugins } from '@/plugins'
import { api as authApi } from '@/api/auth'
import store, { setupStore } from '@/store'
import setupInterceptors from '@/utils/axios-interceptors'
import { setupI18n } from '@/plugins/i18n'
import router, { setupRouter } from '@/plugins/router'
import { setupImporter } from '@/plugins/importer'
import { setupToastComponents } from '@/plugins/toast-components'
import { setupSentry } from '@/plugins/sentry'
import { bindSentryToStore } from '@/plugins/sentry/store-bindings'
import { createHead } from '@unhead/vue/client'

import '@mdi/font/css/materialdesignicons.css'
import '@/styles/styles.scss'
import VueVirtualScroller from 'vue-virtual-scroller'

import importToastManager from '@/services/import-toast-manager'

import App from './App.vue'

const {
  preferences, axios, i18n,
} = plugins

setupInterceptors(axios)
setupInterceptors(authApi)

// Vue.config.productionTip = false

//
document.documentElement.classList.add(`target--${BUILD_TARGET}`)

const app = createApp(App)

// Setup all Vue 3 plugins that need the app instance (includes eventbus, safe-area-insets, error-handler, toasts, modal)
setupPlugins(app)

// Register toast components globally (must be before any components that might use them)
setupToastComponents(app)

// Register global constants for template access
app.config.globalProperties.$appVersion = APPVERSION
app.config.globalProperties.$gitSha = GITSHA

// Setup router with Vue 3 compatible setup function
setupRouter(app)
// Setup store with Vue 3 compatible setup function
setupStore(app)
// i18n requires special setup instead of just app.use(i18n)
setupI18n(app)
// Setup importer
setupImporter(app)

// Initialize Sentry — must be before app.mount so the Vue error handler installs.
// No-op if CONFIG.sentry.dsn is empty.
setupSentry(app, router)
bindSentryToStore(store, router)

app.use(VueVirtualScroller)

const head = createHead()
app.use(head)

// The following is a more appropriate way to handle the Vue 2 mounted hook in Vue 3
// Store original mount function
const originalMount = app.mount

// Override mount to include our additional logic
app.mount = selector => {
  // Call the original mount function
  const instance = originalMount(selector)

  // Execute mounted logic (this runs after the app is mounted)
  if (import.meta.env.DEV) {
    window.vm = app
  }

  // Configure preferences (needs to be awaited)
  preferences.configure({ group: 'RubraStorage' }).then(() => {
    // Emit app created event using global properties
    app.config.globalProperties.$bus.emit('app:created', app)
  })

  return instance
}

// Initialize global import toast manager for all contexts (including extension popup)
// This is needed to show bulk import progress in the extension
import('@/services/import-toast-manager').then(({ default: importToastManager }) => {
  importToastManager.initialize()
})

// Clean up any stale sync imports from localStorage (not needed in extension popup)
if (window.RUBRA_EXTENSION_ENV !== 'popup') {
  try {
    const storedImport = localStorage.getItem('activeImport')
    if (storedImport) {
      const importData = JSON.parse(storedImport)
      if (importData?.importId?.startsWith('sync-')) {
        console.log('[Main] Cleaning up stale sync import from localStorage')
        localStorage.removeItem('activeImport')
      }
    }
  } catch (error) {
    console.error('[Main] Failed to clean up stale import data:', error)
  }
}

// Mount the app
app.mount('#app')

// After mount, proactively recover import toast across routes
setTimeout(() => {
  try {
    console.log('[Main] Checking pending import after mount')
    importToastManager.checkPendingImport()
  } catch (error) {
    console.error('[Main] Failed to check pending import:', error)
  }
}, 0)

// new Vue({
//   router,
//   store,
//   i18n,
//   vuetify,
//   async mounted() {
//     if (import.meta.env.DEV) {
//       window.vm = this
//     }

//     // this needs mounted hook
//     await preferences.configure({ group: 'RubraStorage' })
//     this.$bus.emit('app:created', this)
//   },
//   render: h => h(App),
// }).$mount('#app')
